import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useT } from '../i18n/i18n';
import AuditTab from './AuditTab';
import ClassesTab from './ClassesTab';
import OverviewTab from './OverviewTab';
import ParentsTab from './ParentsTab';
import PupilsTab from './PupilsTab';
import SchoolSettingsTab from './SchoolSettingsTab';
import StaffTab from './StaffTab';
import SchoolTour, { TOUR_SEEN_KEY, tourSteps } from './SchoolTour';
import { logoUrl, roleLabel, useSchool } from './schoolApi';
import './school.css';

const TABS = [
  { id: 'overview', label: 'Overview', admin: false },
  { id: 'pupils', label: 'Pupils', admin: false },
  { id: 'classes', label: 'Classes', admin: false },
  { id: 'parents', label: 'Parents', admin: false },
  { id: 'staff', label: 'Staff', admin: true },
  { id: 'settings', label: 'Settings', admin: true },
  { id: 'audit', label: 'Audit log', admin: true },
] as const;

type TabId = (typeof TABS)[number]['id'];

const TAB_KEY = 'el.school.tab';

function rememberedTab(): TabId {
  try {
    const v = localStorage.getItem(TAB_KEY);
    if (v && TABS.some((x) => x.id === v)) return v as TabId;
  } catch { /* storage off */ }
  return 'overview';
}

export default function SchoolPage() {
  const t = useT();
  const { school, loaded } = useSchool();
  const [tab, setTab] = useState<TabId>(rememberedTab);
  const [logo, setLogo] = useState<string | null>(null);
  const [tour, setTour] = useState<number | null>(null);

  useEffect(() => {
    try { localStorage.setItem(TAB_KEY, tab); } catch { /* storage off */ }
  }, [tab]);

  useEffect(() => {
    let url: string | null = null;
    let live = true;
    if (school?.hasLogo) void logoUrl().then((u) => { url = u; if (live) setLogo(u); });
    else setLogo(null);
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [school?.hasLogo]);

  useEffect(() => {
    if (!school) return;
    let seen = false;
    try { seen = localStorage.getItem(TOUR_SEEN_KEY) === '1'; } catch { /* storage off */ }
    if (!seen) setTour(0);
  }, [school?.staffId]);

  function goTo(step: number) {
    if (!school) return;
    setTour(step);
    setTab(tourSteps(school.role)[step].tab);
  }

  function closeTour() {
    setTour(null);
    try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch { /* storage off */ }
  }

  if (!loaded) return <p className="muted">{t('Loading…')}</p>;
  if (!school || school.role === 'PARENT') return <Navigate to="/" replace />;

  const admin = school.role === 'ADMIN';
  const shown = TABS.filter((x) => admin || !x.admin);
  const current = shown.some((x) => x.id === tab) ? tab : 'overview';

  return (
    <section aria-labelledby="school-h" className="spaced">
      <div className="school-head">
        {logo && <img src={logo} alt="" className="school-logo" />}
        <div>
          <h2 id="school-h" style={{ margin: 0 }}>{school.name}</h2>
          <p className="muted" style={{ margin: 0 }}>{t('You are signed in as: {role}', { role: t(roleLabel(school.role)) })}</p>
        </div>
        {tour === null && <button type="button" className="btn ghost school-tour-open" onClick={() => goTo(0)}>{t('Take the tour')}</button>}
      </div>

      {tour !== null && <SchoolTour role={school.role} step={tour} onStep={goTo} onClose={closeTour} />}

      <div className="tabs" role="tablist" aria-label={t('School')}>
        {shown.map((x) => (
          <button
            key={x.id}
            id={`school-tab-${x.id}`}
            className={`tab${current === x.id ? ' is-active' : ''}`}
            role="tab"
            type="button"
            aria-selected={current === x.id}
            aria-controls={`school-panel-${x.id}`}
            onClick={(e) => { setTab(x.id); e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }}
          >
            {t(x.label)}
          </button>
        ))}
      </div>

      <div id={`school-panel-${current}`} role="tabpanel" aria-labelledby={`school-tab-${current}`}>
        {current === 'overview' && <OverviewTab school={school} />}
        {current === 'pupils' && <PupilsTab school={school} />}
        {current === 'classes' && <ClassesTab school={school} />}
        {current === 'parents' && <ParentsTab />}
        {current === 'staff' && admin && <StaffTab />}
        {current === 'settings' && admin && <SchoolSettingsTab school={school} />}
        {current === 'audit' && admin && <AuditTab />}
      </div>
    </section>
  );
}
